import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import axios from "axios";
import _ from "lodash";

import { toastError } from "../../../shared/toastHelper";
import "../../../shared/styles/mySettingsStyles.css";

// Venue-manager notification preferences. `requiresEmail` rows only show when the
// membership receives email notifications; `requiresPaymentReviews` also gates on
// the caller's PaymentReview access. Mirrors the previous inline conditionals.
const VENUE_MANAGER_PREFS = [
  { key: "send_daily_activity_summary", label: "Daily Activity Summary", desc: "Daily recap of the previous days holds and confirms in Opendate.", requiresEmail: true },
  { key: "send_weekly_activity_summary", label: "Weekly Activity Summary", desc: "Weekly recap of the previous weeks holds and confirms in Opendate.", requiresEmail: true },
  { key: "send_daily_ticket_counts", label: "Daily Ticket Counts", desc: "Daily recap of ticket counts for all current on-sale shows.", requiresEmail: true },
  { key: "send_weekly_ticket_counts", label: "Weekly Ticket Counts", desc: "Weekly recap of ticket counts for all current on-sale shows.", requiresEmail: true },
  { key: "send_event_updated_notifications", label: "Event Updated Notification", desc: "Notifies you whenever someone on your team updates an event." },
  { key: "send_event_confirmed_notifications", label: "Event Confirmed Notification", desc: "Notifies you when an event is confirmed or published in Opendate." },
  { key: "send_inbound_submission_notifications", label: "Inbound Submission Notification", desc: "Notifies you whenever an Inbound Submission is received." },
  { key: "send_financial_notifications", label: "Financial Notifications", desc: "Notifies you when important financial activities need attention.", requiresEmail: true },
  { key: "send_daily_risk_digest", label: "Financial Risk & Chargebacks", desc: "Morning summary of new flagged orders and any automatic refunds taken overnight.", requiresEmail: true, requiresPaymentReviews: true },
  { key: "send_staffing_alert_digest", label: "Staffing Alerts", desc: "Emails every 15 minutes when staff are late, miss a clock-out, near overtime or need a time card reviewed.", requiresStaffing: true },
  { key: "send_conductor_cue_emails", label: "Conductor Cue Emails", desc: "Emails of cue results teammates send you from Conductor." },
];

const canSubmitForm = (originalTeamMembership, teamMembership, persistedFanSubscriptions, fanSubscriptionState) => {
  var teamMembershipHasChanges = !!teamMembership && VENUE_MANAGER_PREFS.some(
    (pref) => originalTeamMembership[pref.key] !== teamMembership[pref.key]
  );

  var fanSubscriptionHasChanges = !_.isEqual(persistedFanSubscriptions, fanSubscriptionState);

  return teamMembershipHasChanges || fanSubscriptionHasChanges;
}

const PrefToggle = ({ pref, checked, onChange }) => {
  const id = pref.key.replace(/_/g, "-");
  return (
    <div className="settings-pref-row">
      <div>
        <label className="settings-pref-name" htmlFor={id}>{pref.label}</label>
        <p className="settings-pref-desc">{pref.desc}</p>
      </div>
      <label className="settings-toggle">
        <input type="checkbox" id={id} checked={checked} onChange={onChange} />
        <span className="settings-toggle-track" />
      </label>
    </div>
  );
};

PrefToggle.propTypes = {
  pref: PropTypes.shape({ key: PropTypes.string, label: PropTypes.string, desc: PropTypes.string }).isRequired,
  checked: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
};

const EmailNotificationSettings = ({
  csrfToken,
  team,
  teamMembership,
  teamMembershipChanged,
  originalTeamMembership,
  updateTeamMembership,
  venueOwnershipFanSubscriptions,
  canManagePaymentReviews,
  canAccessStaffing,
}) => {
  const [persistedFanSubscriptions, setPersistedFanSubscriptions] = useState(venueOwnershipFanSubscriptions);
  const [fanSubscriptionState, setFanSubscriptionState] = useState(persistedFanSubscriptions);
  const [mailkickSubscriptionsToDelete, setMailkickSubscriptionsToDelete] = useState([]);
  const [mailkickSubscriptionsToCreate, setMailkickSubscriptionsToCreate] = useState([]);

  const receivesEmail = !!teamMembership?.receives_email_notifications;
  const visiblePrefs = VENUE_MANAGER_PREFS.filter((pref) => (
    (!pref.requiresEmail || receivesEmail)
      && (!pref.requiresPaymentReviews || canManagePaymentReviews)
      && (!pref.requiresStaffing || canAccessStaffing)
  ));

  const handleMailkickSubscriptionToggle = (e, method, subscription) => {
    setFanSubscriptionState(fanSubscriptionState.map((sub) => {
      if(sub.venue_ownership_id === subscription.venue_ownership_id) {
        return { ...sub, [method]: e.target.checked };
      }
      return sub;
    }));
  };

  useEffect(() => {
    const newSubscriptionsToDelete = [];
    const newSubscriptionsToCreate = [];

    for (let index = 0; index < fanSubscriptionState.length; index++) {
      let persistedSubscription = persistedFanSubscriptions[index];
      let stateSubscription = fanSubscriptionState[index];

      if (stateSubscription.email_opt_in && !persistedSubscription.email_opt_in) {
        newSubscriptionsToCreate.push({
          fan_id: stateSubscription.fan_id,
          venue_ownership_id: stateSubscription.venue_ownership_id,
          user_id: stateSubscription.fan_id,
          list: `MARKETING_EMAIL-${stateSubscription.venue_ownership_id}`,
        });
      } else if (!stateSubscription.email_opt_in && persistedSubscription.email_opt_in) {
        newSubscriptionsToDelete.push({
          fan_id: stateSubscription.fan_id,
          venue_ownership_id: stateSubscription.venue_ownership_id,
          user_id: stateSubscription.fan_id,
          list: `MARKETING_EMAIL-${stateSubscription.venue_ownership_id}`,
        });
      }

      if (stateSubscription.sms_opt_in && !persistedSubscription.sms_opt_in) {
        newSubscriptionsToCreate.push({
          fan_id: stateSubscription.fan_id,
          venue_ownership_id: stateSubscription.venue_ownership_id,
          user_id: stateSubscription.fan_id,
          list: `MARKETING_SMS-${stateSubscription.venue_ownership_id}`,
        });
      } else if (!stateSubscription.sms_opt_in && persistedSubscription.sms_opt_in) {
        newSubscriptionsToDelete.push({
          fan_id: stateSubscription.fan_id,
          venue_ownership_id: stateSubscription.venue_ownership_id,
          user_id: stateSubscription.fan_id,
          list: `MARKETING_SMS-${stateSubscription.venue_ownership_id}`,
        });
      }
    };

    setMailkickSubscriptionsToDelete(newSubscriptionsToDelete);
    setMailkickSubscriptionsToCreate(newSubscriptionsToCreate);
  }, [fanSubscriptionState]);

  const handleSubmit = (e) => {
    e.preventDefault();

    if(!canSubmitForm(originalTeamMembership, teamMembership, persistedFanSubscriptions, fanSubscriptionState)){
      return false;
    }

    let promises = [];

    axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;

    mailkickSubscriptionsToDelete.map((subscription) => {
      promises.push(axios.post(`/fans/${subscription.fan_id}/mailkick_subscriptions/unsubscribe`, {
        list: subscription.list,
      }));
    });

    mailkickSubscriptionsToCreate.map((subscription) => {
      promises.push(axios.post(`/fans/${subscription.fan_id}/mailkick_subscriptions/subscribe`, {
        list: subscription.list,
      }));
    });

    Promise.all(promises)
      .then(() => {
        setMailkickSubscriptionsToDelete([]);
        setMailkickSubscriptionsToCreate([]);
        setPersistedFanSubscriptions(fanSubscriptionState);
        if (teamMembership) updateTeamMembership(csrfToken, team, teamMembership);
      })
      .catch((_error) => {
        toastError("Failed to update email notification settings");
      });
  };

  const disabled = !canSubmitForm(originalTeamMembership, teamMembership, persistedFanSubscriptions, fanSubscriptionState);

  return (
    <section className="settings-card" data-settings-section id="notifications">
      <form onSubmit={handleSubmit}>
        <div className="settings-card-header">
          <h2 className="settings-card-title">Notifications</h2>
          <p className="settings-card-desc">Choose which venues and updates you hear from.</p>
        </div>
        <div className="settings-card-body">
          {fanSubscriptionState.length ? (
            <div className="settings-venue-table">
              <div className="settings-venue-row settings-venue-row--head">
                <span>Venue</span>
                <span className="settings-venue-cell">Email</span>
                <span className="settings-venue-cell">SMS</span>
              </div>
              {fanSubscriptionState.map((subscription) => (
                <div className="settings-venue-row" key={subscription.venue_ownership_id}>
                  <span className="settings-venue-name">{subscription.name}</span>
                  <span className="settings-venue-cell">
                    <input
                      className="settings-checkbox"
                      type="checkbox"
                      checked={subscription.email_opt_in}
                      onChange={(e) => handleMailkickSubscriptionToggle(e, "email_opt_in", subscription)}
                      id={`email-opt-in-${subscription.venue_ownership_id}`}
                      aria-label={`Email from ${subscription.name}`}
                    />
                  </span>
                  <span className="settings-venue-cell">
                    <input
                      className="settings-checkbox"
                      type="checkbox"
                      checked={subscription.sms_opt_in}
                      onChange={(e) => handleMailkickSubscriptionToggle(e, "sms_opt_in", subscription)}
                      id={`sms-opt-in-${subscription.venue_ownership_id}`}
                      aria-label={`SMS from ${subscription.name}`}
                    />
                  </span>
                </div>
              ))}
            </div>
          ) : null}

          {team?.setup_complete && teamMembership ? (
            <div style={{ marginTop: fanSubscriptionState.length ? "var(--spacing-3xl, 24px)" : 0 }}>
              <p className="settings-subsection-label">Venue Manager</p>
              <p className="settings-pref-desc" style={{ marginBottom: "var(--spacing-md, 8px)" }}>
                Updates from Opendate about events you are organizing.
              </p>
              {visiblePrefs.map((pref) => (
                <PrefToggle
                  key={pref.key}
                  pref={pref}
                  checked={!!teamMembership[pref.key]}
                  onChange={(e) => teamMembershipChanged(Object.assign({}, teamMembership, { [pref.key]: e.target.checked }))}
                />
              ))}
            </div>
          ) : null}
        </div>
        <div className="settings-card-footer">
          <button type="submit" className="settings-btn" disabled={disabled}>
            Save changes
          </button>
        </div>
      </form>
    </section>
  );
};

EmailNotificationSettings.propTypes = {
  csrfToken: PropTypes.string.isRequired,
  currentUser: PropTypes.object.isRequired,
  team: PropTypes.object,
  teamMembership: PropTypes.object,
  teamMembershipChanged: PropTypes.func.isRequired,
  originalTeamMembership: PropTypes.object,
  updateTeamMembership: PropTypes.func.isRequired,
  venueOwnershipFanSubscriptions: PropTypes.array,
  canManagePaymentReviews: PropTypes.bool,
  canAccessStaffing: PropTypes.bool
};

EmailNotificationSettings.defaultProps = {
  canManagePaymentReviews: false,
  canAccessStaffing: false,
  team: null,
  teamMembership: null,
  originalTeamMembership: null,
};

export default EmailNotificationSettings;
